import React, { useState } from 'react';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TouchableOpacity, TextInput, ActivityIndicator, Alert } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Button } from '../components/Button';
import { Colors, SIZES } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import MapView from '../components/Map/MapView';
import ScreenBackground from '../components/ScreenBackground';
import { locationService } from '../services/locationService';
import { storageService } from '../services/storageService';
import { firestoreService } from '../services/firestoreService';
import { getCurrentBridgeUser } from '../services/authBridge';
import { volver } from '../utils/volver';
import { useGuardiaEscolar } from '../hooks/useGuardiaEscolar';
import { mensajeDeError } from '../utils/mensajeDeError';

const DocumentUploadRow = ({ title, subtitle, isUploaded, isUploading, onPress }: any) => (
  <TouchableOpacity style={styles.uploadRow} onPress={onPress} disabled={isUploaded || isUploading}>
    <View style={styles.uploadInfo}>
      <Text style={styles.uploadTitle}>{title}</Text>
      {subtitle && <Text style={styles.uploadSubtitle}>{subtitle}</Text>}
    </View>
    <View style={styles.uploadAction}>
      {isUploading ? (
        <ActivityIndicator color={Colors.accent} size="small" />
      ) : isUploaded ? (
        <Ionicons name="checkmark-circle" size={24} color={Colors.accent} />
      ) : (
        <Ionicons name="camera-outline" size={24} color={Colors.primary} />
      )}
    </View>
  </TouchableOpacity>
);

export default function KycDirectorScreen() {
  // ══ EL CANDADO QUE FALTABA ════════════════════════════════════
  //
  // Era la ÚNICA pantalla del vertical escolar sin este hook — las otras seis
  // lo tienen— y además estaba en `publicPaths`, o sea alcanzable sin sesión.
  // Esconder la tarjeta no basta: la URL de expo-router sigue existiendo.
  //
  // Y no era inofensivo: aquí se escribe `users/{uid}.schoolName`, y la
  // inferencia de rol de `_layout.tsx` concluía de ese campo que la cuenta era
  // SCHOOL_DIRECTOR en el siguiente arranque. O sea que el vertical anulado se
  // resucitaba solo, y aterrizaba en un panel que se auto-expulsa: pantalla en
  // blanco y de vuelta a la portada. Las dos puntas se cierran: el candado
  // aquí, y la rama de inferencia fuera de _layout.tsx.
  const escolarOculto = useGuardiaEscolar();
  const router = useRouter();
  // Bridge UID — cubre native + REST (mismo motivo que kyc-documents.tsx).
  const storeUid = useAppStore((s) => s.userFirebaseUid);
  const userFirebaseUid = getCurrentBridgeUser()?.uid ?? storeUid;
  const [instituteName, setInstituteName] = useState('');
  const [isVerifyingLocation, setIsVerifyingLocation] = useState(false);
  const [locationVerified, setLocationVerified] = useState(false);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [uploadedDocs, setUploadedDocs] = useState<Record<string, string>>({}); // titulo → URL del storage
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);

  const handleUpload = async (title: string) => {
    if (!userFirebaseUid) {
      Alert.alert('Sesión requerida', 'Inicia sesión para subir tus documentos verificables.');
      return;
    }
    try {
      // Cámara obligatoria — el bloque amarillo arriba dice "foto en vivo".
      const perm = await ImagePicker.requestCameraPermissionsAsync();
      if (perm.status !== 'granted') {
        Alert.alert('Permiso denegado', 'Necesitamos acceso a la cámara para tomar la foto del carnet.');
        return;
      }
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ['images'],
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.85,
        // Pasamos base64 al storageService para que use el endpoint
        // backend con Admin SDK (no necesita sesión nativa Firebase).
        base64: true,
      });
      if (result.canceled || !result.assets?.[0]) return;

      setUploadingDoc(title);
      const documentType = title.replace(/\s+/g, '_').toLowerCase();
      const url = await storageService.uploadKYCDocument(
        userFirebaseUid,
        documentType,
        result.assets[0].uri,
        result.assets[0].base64 ?? null
      );
      setUploadedDocs(prev => ({ ...prev, [title]: url }));
    } catch (e: any) {
      console.error('upload doc error', e);
      Alert.alert('Error al subir', mensajeDeError(e, 'No pudimos subir el documento. Intenta de nuevo.'));
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleVerifyLocation = async () => {
    if (!instituteName.trim()) {
      Alert.alert('Falta Información', 'Por favor ingresa el nombre de la institución primero para poder rastrear su ubicación.');
      return;
    }
    setIsVerifyingLocation(true);
    try {
      // Pedimos la ubicación actual real desde la directora — asume que está
      // físicamente en el colegio para validar las coordenadas.
      const loc = await locationService.getCurrentLocation();
      if (!loc) {
        Alert.alert('Permiso denegado', 'No pudimos acceder a tu ubicación. Activa el GPS y vuelve a intentar.');
        return;
      }
      setCoords({ lat: loc.coords.latitude, lng: loc.coords.longitude });
      setLocationVerified(true);
    } catch (e: any) {
      console.warn('verify location error', e);
      Alert.alert('Error', 'No se pudo obtener la ubicación.');
    } finally {
      setIsVerifyingLocation(false);
    }
  };

  const handleNext = async () => {
    if (!instituteName.trim()) {
      Alert.alert('Información Incompleta', 'Por favor ingresa el nombre de la institución o escuela.');
      return;
    }
    if (!locationVerified || !coords) {
      Alert.alert('Ubicación No Verificada', 'Por favor presiona "Usar mi ubicación actual" para guardar las coordenadas de la escuela.');
      return;
    }
    if (!uploadedDocs['Carnet de la Institución']) {
      Alert.alert('Falta Documento', 'Por favor captura una foto en vivo de tu Carnet de Directora.');
      return;
    }
    if (!userFirebaseUid) {
      Alert.alert('Sesión requerida', 'Tu sesión expiró. Vuelve a iniciar sesión.');
      return;
    }

    try {
      // Persistir el perfil del director en /users/{uid} antes de pasar a biometría.
      // El schoolId del director es su propio uid (un director gestiona una escuela).
      // ⚠️ SIN `role`. Ver la nota de arriba: las reglas lo prohiben desde el
      // cliente y, al denegarse la escritura COMPLETA, se perdia con el el
      // carnet y las coordenadas del colegio.
      await firestoreService.updateUserProfile(userFirebaseUid, {
        schoolName: instituteName.trim(),
        schoolLat: coords.lat,
        schoolLng: coords.lng,
        kycCarnetUrl: uploadedDocs['Carnet de la Institución'],
        kycStatus: 'PENDING_BIOMETRICS',
      });
    } catch (e: any) {
      // ESTO SI BLOQUEA. Antes era un `console.warn` y se seguia a la
      // biometria: la directora terminaba convencida de haber mandado su
      // carnet y en el panel no aparecia nada que aprobar. Sin este guardado
      // no hay solicitud que revisar, asi que es mejor decirlo aqui.
      console.warn('No se pudo guardar el perfil de la directora:', e?.message);
      Alert.alert(
        'No pudimos guardar tus datos',
        mensajeDeError(e, 'Revisa tu conexión e intenta de nuevo. Sin este paso no podemos revisar tu carnet.')
      );
      return;
    }

    router.push('/kyc-biometrics');
  };

  // Después de todos los hooks: React exige que el número de hooks no
  // cambie entre renders, así que el corte va aquí y no arriba.
  if (escolarOculto) return null;
  return (
    <ScreenBackground>
      <SafeAreaView style={styles.safeArea}>
        <ScrollView contentContainerStyle={styles.container}>
        <TouchableOpacity onPress={() => {
          if (router.canGoBack()) {
            volver(router);
          } else {
            router.replace('/');
          }
        }} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={Colors.text} />
        </TouchableOpacity>
        <View style={styles.header}>
          <Text style={styles.title}>Verificación Institucional</Text>
          <Text style={styles.subtitle}>
            Para garantizar la seguridad de los estudiantes, validaremos tus credenciales como directora escolar.
          </Text>
        </View>

        <View style={{ backgroundColor: 'rgba(255, 149, 0, 0.1)', padding: 16, borderRadius: 12, marginBottom: 16, flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: 'rgba(255, 149, 0, 0.3)' }}>
          <Ionicons name="shield-checkmark" size={28} color="#FF9500" style={{ marginRight: 12 }} />
          <Text style={{ flex: 1, fontSize: 13, color: '#D07B00', lineHeight: 18 }}>
            <Text style={{ fontWeight: 'bold' }}>Solo Validación Institucional:</Text> No requerimos cédula. La foto de su carnet escolar debe ser tomada <Text style={{ fontWeight: 'bold', textDecorationLine: 'underline' }}>en vivo en este momento</Text> para validar autenticidad.
          </Text>
        </View>

        <View style={styles.card}>
          <Text style={styles.sectionHeader}>Datos de la Escuela</Text>
          <View style={styles.inputContainer}>
            <Text style={styles.inputLabel}>Nombre del Instituto Escolar</Text>
            <TextInput
              style={styles.input}
              placeholder="Ej: Colegio San Agustín"
              value={instituteName}
              onChangeText={setInstituteName}
            />
          </View>
          
          <TouchableOpacity 
            style={[styles.verifyLocationBtn, locationVerified && styles.verifyLocationSuccess]}
            onPress={handleVerifyLocation}
            disabled={locationVerified || isVerifyingLocation}
          >
            {isVerifyingLocation ? (
              <ActivityIndicator color="#FFF" size="small" />
            ) : (
              <>
                <Ionicons 
                  name={locationVerified ? "checkmark-circle" : "location"} 
                  size={18} 
                  color="#FFF" 
                  style={{ marginRight: 8 }}
                />
                <Text style={styles.verifyLocationText}>
                  {locationVerified ? 'Ubicación de la escuela guardada' : 'Usar mi ubicación actual'}
                </Text>
              </>
            )}
          </TouchableOpacity>

          {locationVerified && (
            <View style={styles.mapContainer}>
              <MapView 
                style={styles.map}
                initialRegion={{
                  latitude: 18.4861,
                  longitude: -69.9312,
                  latitudeDelta: 0.005,
                  longitudeDelta: 0.005
                }}
              />
              <View style={styles.mapLabelOverlay}>
                <Ionicons name="radio" size={14} color="#00C853" style={{ marginRight: 4 }} />
                <Text style={styles.mapLabelText}>Coordenadas Validadas</Text>
              </View>
            </View>
          )}
        </View>

        <View style={styles.card}>
          <Text style={styles.sectionHeader}>Documento Escolar</Text>
          <DocumentUploadRow 
            title="Carnet de la Institución" 
            subtitle="Tómale una foto a tu carnet de Directora" 
            isUploaded={!!uploadedDocs['Carnet de la Institución']}
            isUploading={uploadingDoc === 'Carnet de la Institución'}
            onPress={() => handleUpload('Carnet de la Institución')}
          />
        </View>

        <View style={styles.infoBox}>
          <Text style={{fontSize: 20, marginRight: 12}}>🔒</Text>
          <Text style={styles.infoText}>
            En el siguiente paso te pediremos una selfie. Una persona de nuestro equipo la compara con tu carnet antes de aprobarte.
          </Text>
        </View>
      </ScrollView>
        {/* El botón decía "Continuar a Biometría". El paso siguiente
            (kyc-biometrics.tsx) se titula en pantalla "Selfie de verificación" y
            eso es exactamente lo que pide: una foto que revisa una persona a
            mano. Llamarlo biometría contradice terms.tsx punto 4 y privacy.tsx
            punto 2.c — y es la palabra que ya costó un rechazo de Google Play.
            La pantalla de documentos ya se corrigió igual (kyc-documents.tsx);
            esta se había quedado fuera. */}
        <View style={styles.footer}>
          <Button 
            title="Validar y Continuar a la Selfie" 
            onPress={handleNext} 
          />
        </View>
      </SafeAreaView>
    </ScreenBackground>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flexGrow: 1,
    padding: SIZES.padding,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    paddingBottom: 40,
  },
  backButton: {
    padding: 8,
    alignSelf: 'flex-start',
    marginLeft: -8,
    marginBottom: 8,
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Colors.text,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: SIZES.font,
    color: Colors.textSecondary,
    lineHeight: 22,
  },
  card: {
    backgroundColor: Colors.card,
    borderRadius: SIZES.radius,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 8, elevation: 5,
  },
  sectionHeader: {
    fontSize: 16,
    fontWeight: 'bold',
    color: Colors.text,
    marginBottom: 16,
  },
  inputContainer: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.textSecondary,
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: Colors.border,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: Colors.background,
  },
  uploadRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  uploadInfo: {
    flex: 1,
  },
  uploadTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: Colors.text,
    marginBottom: 4,
  },
  uploadSubtitle: {
    fontSize: 12,
    color: Colors.textSecondary,
  },
  uploadAction: {
    width: 40,
    alignItems: 'center',
  },
  infoBox: {
    flexDirection: 'row',
    backgroundColor: '#F5F5F5',
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 8,
  },
  infoText: {
    flex: 1,
    fontSize: 12,
    color: Colors.textSecondary,
    lineHeight: 18,
  },
  verifyLocationBtn: {
    backgroundColor: Colors.primary,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderRadius: 8,
    marginTop: 4,
    marginBottom: 8,
  },
  verifyLocationSuccess: {
    backgroundColor: '#4CAF50',
  },
  verifyLocationText: {
    color: '#FFF',
    fontWeight: 'bold',
    fontSize: 14,
  },
  mapContainer: {
    height: 150,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: Colors.border,
    marginTop: 12,
    position: 'relative',
  },
  map: {
    flex: 1,
  },
  mapLabelOverlay: {
    position: 'absolute',
    top: 8,
    left: 8,
    backgroundColor: 'rgba(255,255,255,0.9)',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: 'rgba(0, 200, 83, 0.3)',
  },
  mapLabelText: {
    color: '#00C853',
    fontSize: 10,
    fontWeight: 'bold',
  },
  footer: {
    padding: SIZES.padding,
    backgroundColor: Colors.card,
    borderTopWidth: 1,
    borderTopColor: Colors.border,
  }
});
